(() => {
  if (customElements.get('hero-media')) return;

  class HeroMedia extends HTMLElement {
    constructor() {
      super();
      this.breakpoint = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoLayer = this.querySelector('[data-hero-video-layer]');
      this.posterImage = this.querySelector('.hero-media__poster-image');
      this.modalId = this.querySelector('[data-hero-film-opener]')?.getAttribute('data-modal');
      this.wasBackgroundPlaying = false;
      this.posterReady = false;

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleFilmIntent = this.handleFilmIntent.bind(this);
      this.handleFilmShow = this.handleFilmShow.bind(this);
      this.handleFilmOpen = this.handleFilmOpen.bind(this);
      this.handleFilmCloseRequest = this.handleFilmCloseRequest.bind(this);
      this.handleFilmHide = this.handleFilmHide.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
      this.handlePageLifecycle = this.handlePageLifecycle.bind(this);
    }

    connectedCallback() {
      this.breakpoint.addEventListener('change', this.handleBreakpointChange);
      this.reducedMotion.addEventListener('change', this.handleMotionChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.body.addEventListener('modalClosed', this.handleModalClosed);
      window.addEventListener('pageshow', this.handlePageLifecycle);
      window.addEventListener('pagehide', this.handlePageLifecycle);
      const filmButton = this.querySelector('[data-hero-film-opener] button');
      filmButton?.addEventListener('click', this.handleFilmIntent, true);
      this.configureFilmModal();
      this.resetFeatureVideo();
      this.prepareBackground();
    }

    disconnectedCallback() {
      this.breakpoint.removeEventListener('change', this.handleBreakpointChange);
      this.reducedMotion.removeEventListener('change', this.handleMotionChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.body.removeEventListener('modalClosed', this.handleModalClosed);
      window.removeEventListener('pageshow', this.handlePageLifecycle);
      window.removeEventListener('pagehide', this.handlePageLifecycle);
      const filmButton = this.querySelector('[data-hero-film-opener] button');
      filmButton?.removeEventListener('click', this.handleFilmIntent, true);
      this.modalCloseButton?.removeEventListener('click', this.handleFilmCloseRequest, true);
      clearTimeout(this.filmCloseTimer);
      if (this.modal && this.originalModalShow && this.originalModalHide) {
        this.modal.show = this.originalModalShow;
        this.modal.hide = this.originalModalHide;
      }
      this.posterReady = false;
      this.removeBackground();
      this.modal?.remove();
    }

    get dataSaverEnabled() {
      return Boolean(navigator.connection?.saveData);
    }

    get modal() {
      return this.modalId ? document.querySelector(this.modalId) : null;
    }

    get featureVideo() {
      return this.modal?.querySelector('video.hero-film-modal__video');
    }

    get backgroundVideo() {
      return this.videoLayer?.querySelector('video');
    }

    configureFilmModal() {
      const modal = this.modal;
      if (!modal || this.originalModalHide) return;

      this.originalModalShow = modal.show.bind(modal);
      this.originalModalHide = modal.hide.bind(modal);
      modal.show = this.handleFilmShow;
      modal.hide = this.handleFilmHide;
      this.modalCloseButton = modal.querySelector('.hero-film-modal__close');
      this.modalCloseButton?.addEventListener('click', this.handleFilmCloseRequest, true);
    }

    getBackgroundTemplate() {
      const preferred = this.breakpoint.matches ? 'mobile' : 'desktop';
      return this.querySelector(`[data-hero-background-template="${preferred}"]`);
    }

    async prepareBackground() {
      if (this.posterImage) {
        try {
          await this.posterImage.decode();
        } catch {
          if (!this.posterImage.complete) {
            await new Promise((resolve) => {
              this.posterImage.addEventListener('load', resolve, { once: true });
              this.posterImage.addEventListener('error', resolve, { once: true });
            });
          }
        }
      }

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });

      if (!this.isConnected) return;
      this.posterReady = true;
      this.loadBackground();
    }

    loadBackground() {
      if (
        !this.posterReady ||
        !this.videoLayer ||
        this.backgroundVideo ||
        this.reducedMotion.matches ||
        this.dataSaverEnabled
      ) {
        return;
      }

      const template = this.getBackgroundTemplate();
      if (!template) return;

      const video = template.content.firstElementChild.cloneNode(true);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.removeAttribute('poster');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      video.currentTime = 0;
      video.addEventListener(
        'playing',
        () => {
          this.videoLayer.classList.add('is-playing');
        },
        { once: true }
      );

      this.videoLayer.replaceChildren(video);
      const playPromise = video.play();

      if (playPromise) {
        playPromise.catch(() => {
          this.videoLayer.classList.remove('is-playing');
          video.remove();
        });
      }
    }

    removeBackground() {
      this.backgroundVideo?.pause();
      this.videoLayer?.classList.remove('is-playing');
      this.videoLayer?.replaceChildren();
    }

    handleBreakpointChange() {
      this.removeBackground();
      this.loadBackground();
    }

    handleMotionChange() {
      if (this.reducedMotion.matches) {
        this.removeBackground();
      } else {
        this.loadBackground();
      }
    }

    handleVisibilityChange() {
      const video = this.backgroundVideo;
      if (!video) return;

      if (document.hidden) {
        video.pause();
      } else if (!this.modal?.hasAttribute('open')) {
        video.play().catch(() => {});
      }
    }

    handleFilmIntent(event) {
      const background = this.backgroundVideo;
      this.wasBackgroundPlaying = Boolean(background && !background.paused);

      if (!this.modal || !event.currentTarget) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleFilmShow(event.currentTarget);
    }

    handleFilmShow(opener) {
      this.originalModalShow?.(opener);
      this.handleFilmOpen();
    }

    handleFilmOpen() {
      const feature = this.featureVideo;
      if (!feature) return;

      clearTimeout(this.filmCloseTimer);
      this.modal?.classList.remove('is-closing');
      feature.autoplay = true;
      feature.setAttribute('autoplay', '');
      try {
        feature.currentTime = 0;
      } catch {
        // Playback will still begin at zero once the first media data is available.
      }
      feature.removeAttribute('muted');
      feature.defaultMuted = false;
      feature.muted = false;
      const playPromise = feature.play();

      if (playPromise) {
        playPromise.catch(() => {
          feature.muted = true;
          feature.defaultMuted = true;
          feature.setAttribute('muted', '');
          feature
            .play()
            .then(() => {
              feature.removeAttribute('muted');
              feature.defaultMuted = false;
              feature.muted = false;
            })
            .catch(() => {});
        });
      }
    }

    resetFeatureVideo() {
      const feature = this.featureVideo;
      if (!feature) return;

      feature.pause();
      feature.autoplay = false;
      feature.removeAttribute('autoplay');
      feature.muted = true;
      feature.defaultMuted = true;
      feature.setAttribute('muted', '');
      try {
        feature.currentTime = 0;
      } catch {
        // The media may not have metadata yet; it will still begin at its default zero position.
      }
    }

    handleFilmCloseRequest(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleFilmHide();
    }

    handleFilmHide() {
      const modal = this.modal;
      if (!modal?.hasAttribute('open') || modal.classList.contains('is-closing')) return;

      this.featureVideo?.pause();

      if (this.reducedMotion.matches) {
        this.finishFilmHide();
        return;
      }

      modal.classList.add('is-closing');
      clearTimeout(this.filmCloseTimer);
      this.filmCloseTimer = window.setTimeout(() => this.finishFilmHide(), 420);
    }

    finishFilmHide() {
      clearTimeout(this.filmCloseTimer);
      this.filmCloseTimer = null;
      this.modal?.classList.remove('is-closing');
      this.originalModalHide?.();
    }

    hideFilmImmediately() {
      clearTimeout(this.filmCloseTimer);
      this.filmCloseTimer = null;
      this.modal?.classList.remove('is-closing');
      this.resetFeatureVideo();
      if (this.modal?.hasAttribute('open')) this.originalModalHide?.();
    }

    handlePageLifecycle(event) {
      if (event?.type === 'pageshow' && !event.persisted) return;

      const modal = this.modal;

      if (modal?.hasAttribute('open')) {
        this.hideFilmImmediately();
      } else {
        this.resetFeatureVideo();
      }

      if (event?.type === 'pageshow' && event.persisted && this.backgroundVideo) {
        this.backgroundVideo.currentTime = 0;
      }
    }

    handleModalClosed(event) {
      if (!this.modal || event.target !== document.body) return;

      const shouldResumeBackground =
        this.wasBackgroundPlaying && !this.reducedMotion.matches && !this.dataSaverEnabled;
      this.wasBackgroundPlaying = false;

      window.requestAnimationFrame(() => {
        this.resetFeatureVideo();

        if (shouldResumeBackground) {
          this.backgroundVideo?.play().catch(() => {});
        }
      });
    }
  }

  customElements.define('hero-media', HeroMedia);
})();
